import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError } from '../api/client';
import { Spinner } from './States';
import { UpgradeCard } from './UpgradeCard';
import { CATEGORIAS_UPGRADE, nomeAceitaFases, type UpgradeFiltros } from './UpgradeFilters';
import { useI18n } from '../i18n';
import { cleanName } from '../lib/format';
import { useCurrency } from '../hooks/useCurrency';
import { useWeapons } from '../hooks/useWeapons';
import type { ListingModel, SkinGroup, TemplateModel } from '../api/types';

interface Props {
  /** Crédito da seleção do inventário, em reais. Zero quando nada escolhido. */
  credito: number;
  filtros: UpgradeFiltros;
  onFiltros: (patch: Partial<UpgradeFiltros>) => void;
  /**
   * O anúncio vem junto quando o cliente escolheu uma peça específica. Sem ele,
   * o pedido é do template e a loja decide qual peça comprar.
   */
  onEscolher: (variant: TemplateModel, grupo: SkinGroup, anuncio?: ListingModel) => void;
  /** Soma das peças já escolhidas, formatada. Fica no cabeçalho da coluna. */
  total: string;
  recolhido: boolean;
  onRecolher: () => void;
  /** A lista de escolhidas, montada pela página (é ela que fecha o pedido). */
  children?: ReactNode;
}

/** A ordenação gira num botão só, como na barra de uma corretora. */
type Ordem = 'price_desc' | 'price_asc' | 'float';

const ORDENS: { key: Ordem; label: string }[] = [
  { key: 'price_desc', label: 'Preço: Máx' },
  { key: 'price_asc', label: 'Preço: Mín' },
  { key: 'float', label: 'Float' },
];

const num = (texto: string): number | null => {
  if (!texto.trim()) return null;
  const v = Number(texto.replace(',', '.'));
  return Number.isFinite(v) ? v : null;
};

/**
 * A coluna da direita: o que o cliente recebe.
 *
 * A navegação tem três degraus — skin, desgaste e peça — porque é assim que o
 * preço se forma: a mesma AK custa várias vezes mais em Nova de Fábrica, e duas
 * peças do mesmo desgaste ainda diferem por float e pattern. Escolher no
 * último degrau é o que faz o cliente levar exatamente o que viu.
 */
export function SkinFinder({
  credito,
  filtros,
  onFiltros,
  onEscolher,
  total,
  recolhido,
  onRecolher,
  children,
}: Props) {
  const { money } = useCurrency();
  const { loc } = useI18n();
  const { data: weapons } = useWeapons();

  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState<Ordem>('price_desc');
  /** A barra de armas aberta em várias linhas, com a lista inteira à vista. */
  const [armasAbertas, setArmasAbertas] = useState(false);
  const [itens, setItens] = useState<SkinGroup[] | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [aberta, setAberta] = useState<SkinGroup | null>(null);
  /** Desgaste aberto no terceiro nível, com os anúncios daquela peça. */
  const [desgaste, setDesgaste] = useState<TemplateModel | null>(null);
  const [anuncios, setAnuncios] = useState<ListingModel[] | null>(null);
  const [erroAnuncios, setErroAnuncios] = useState<string | null>(null);

  // As categorias marcadas viram os `kind` do catálogo.
  const kinds = useMemo(
    () => filtros.categorias.flatMap((id) => CATEGORIAS_UPGRADE.find((c) => c.id === id)?.kinds ?? []),
    [filtros.categorias],
  );

  /**
   * As armas das categorias marcadas — os chips da barra de cima.
   *
   * Vem da taxonomia da API (/weapons), que já agrupa por `kind`. Categorias
   * sem arma nenhuma (adesivos, caixas) devolvem lista vazia, e aí a barra
   * inteira some em vez de virar uma linha vazia.
   */
  const armas = useMemo(() => {
    if (kinds.length === 0) return [];
    return (weapons?.groups ?? [])
      .filter((g) => kinds.includes(g.kind))
      .flatMap((g) => g.items)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [weapons, kinds]);

  /**
   * Os lados do agente — o segundo nível de "Agentes".
   *
   * Agente não tem arma, então a barra de chips ficaria vazia justo na
   * categoria em que o cliente mais precisa separar (CT e TR vestem coisas
   * bem diferentes). Vem da mesma resposta de /weapons que traz as armas.
   */
  const times = useMemo(
    () => (kinds.includes('agent') ? (weapons?.agentTeams ?? []) : []),
    [weapons, kinds],
  );

  /**
   * Desmarcar a categoria deixa a arma órfã: ela sumiu da barra, mas ainda
   * filtraria o catálogo. Some com ela assim que sair da lista.
   */
  useEffect(() => {
    if (filtros.arma && !armas.some((a) => a.code === filtros.arma)) onFiltros({ arma: null });
  }, [filtros.arma, armas, onFiltros]);

  /** Mesma coisa para o lado: sem "Agentes" marcado ele não tem mais onde aparecer. */
  useEffect(() => {
    if (filtros.time && !times.some((t) => t.key === filtros.time)) onFiltros({ time: null });
  }, [filtros.time, times, onFiltros]);

  const floatMin = num(filtros.floatMin);
  const floatMax = num(filtros.floatMax);

  /**
   * Anúncios do desgaste escolhido.
   *
   * A faixa de float vai para a API — /listings filtra por ela de verdade. É o
   * único degrau em que o float existe: skin e desgaste ainda são categorias.
   */
  useEffect(() => {
    if (!desgaste) return;
    let vivo = true;
    setAnuncios(null);
    setErroAnuncios(null);
    void api
      // scan: false — aqui a lista curta basta, e varrer a skin inteira só
      // para mostrar 24 anúncios gastaria seis chamadas ao Youpin.
      .listings({
        templateId: desgaste.id,
        sort: ordem === 'float' ? 'float_asc' : ordem,
        pageSize: 24,
        limit: 24,
        scan: false,
        floatMin: filtros.floatMin.replace(',', '.') || undefined,
        floatMax: filtros.floatMax.replace(',', '.') || undefined,
        // Fase e gema só existem no anúncio; /listings filtra por elas.
        styles: filtros.estilos.length > 0 ? filtros.estilos.join(',') : undefined,
      })
      .then((r) => {
        // Adesivo não é parâmetro da API, mas vem em cada anúncio lido —
        // o corte aqui é sobre a leva inteira, não sobre a página à vista.
        if (vivo) setAnuncios(filtros.comAdesivos ? r.items.filter((i) => i.hasStickers) : r.items);
      })
      .catch((e) => {
        // A mensagem do servidor diz o que está acontecendo de verdade — um
        // "não consegui" genérico esconde, por exemplo, o limite do Youpin.
        if (vivo) setErroAnuncios(e instanceof ApiError ? e.message : 'Não consegui carregar os anúncios agora.');
      });
    return () => {
      vivo = false;
    };
  }, [desgaste, ordem, filtros.floatMin, filtros.floatMax, filtros.estilos, filtros.comAdesivos]);

  const buscar = useCallback(
    async (force = false) => {
      setCarregando(true);
      try {
        // Teto de preço: o menor entre o que foi digitado e o crédito, quando
        // o cliente pediu para ver só o que ele paga sozinho.
        const tetoCredito = filtros.soCredito && credito > 0 ? Math.floor(credito) : null;
        const tetoDigitado = num(filtros.precoMax);
        const maxPrice =
          tetoCredito !== null && tetoDigitado !== null
            ? Math.min(tetoCredito, tetoDigitado)
            : (tetoCredito ?? tetoDigitado ?? undefined);

        const r = await api.skins({
          // Com uma arma escolhida o recorte já é o mais estreito possível:
          // ?type= é filtro de verdade na API e dispensa kind/category.
          // Sem ela, a API aceita um kind por chamada — com mais de um
          // marcado, pede-se o catálogo inteiro e separa-se no cliente.
          ...(filtros.arma
            ? { type: filtros.arma }
            : kinds.length === 1
              ? { kind: kinds[0] }
              : { category: 'all' }),
          // Uma fase marcada já diz que a skin é Doppler. Mandar isso como
          // busca é o que faz a GRADE encolher — sem isso o recorte por nome
          // aconteceria só sobre as 24 skins da página, e quase tudo sumiria
          // sem motivo aparente. A busca digitada manda mais, quando existe.
          search: busca.trim().length >= 2 ? busca.trim() : filtros.estilos.length > 0 ? 'doppler' : undefined,
          // Float não ordena skin: no primeiro degrau a peça ainda não existe.
          sort: ordem === 'float' ? 'price_desc' : ordem,
          exterior: filtros.exteriors.length > 0 ? filtros.exteriors.join(',') : undefined,
          // StatTrak™ é do template, então filtra já no catálogo.
          st: filtros.st !== 'all' ? filtros.st : undefined,
          // O lado só vai para a API quando "Agentes" está sozinho. Com outra
          // categoria junto, o team=ct cortaria os rifles também — eles têm
          // team nulo —, então o recorte desce para o cliente logo abaixo.
          team: filtros.time && kinds.length === 1 ? filtros.time : undefined,
          minPrice: num(filtros.precoMin) ?? undefined,
          maxPrice: maxPrice ?? undefined,
          // O recorte por nome acontece depois da resposta, então a leva
          // pedida é maior para a grade não vir quase vazia.
          pageSize: (!filtros.arma && kinds.length > 1) || filtros.estilos.length > 0 ? 48 : 24,
          force,
        });
        const porCategoria =
          !filtros.arma && kinds.length > 1 ? r.items.filter((g) => g.kind && kinds.includes(g.kind)) : r.items;
        // Agente fora do lado escolhido sai; o que não é agente passa direto,
        // senão marcar CT esvaziaria as outras categorias marcadas junto.
        const porTime =
          filtros.time && kinds.length > 1
            ? porCategoria.filter((g) => g.kind !== 'agent' || g.team === filtros.time)
            : porCategoria;
        // Gamma Doppler não tem Safira, Doppler não tem Esmeralda: o nome da
        // skin basta para tirar da grade o que não pode ter a fase pedida.
        setItens(porTime.filter((g) => nomeAceitaFases(g.name, filtros.estilos)));
      } catch {
        setItens([]);
      } finally {
        setCarregando(false);
      }
    },
    [
      busca,
      kinds,
      filtros.arma,
      filtros.time,
      ordem,
      filtros.soCredito,
      filtros.precoMin,
      filtros.precoMax,
      filtros.exteriors,
      filtros.st,
      filtros.estilos,
      credito,
    ],
  );

  // Espera a digitação parar; sem termo, a lista já vem preenchida.
  useEffect(() => {
    const timer = window.setTimeout(() => void buscar(), busca ? 400 : 0);
    return () => window.clearTimeout(timer);
  }, [buscar, busca]);

  /**
   * Desgastes que sobrevivem à faixa de float pedida.
   *
   * O catálogo não guarda o float de cada peça — só a faixa do desgaste. Dá
   * para cortar aqui o que é impossível (pedir 0.00–0.07 não pode mostrar
   * Veterana de Guerra) e deixar o filtro fino para o degrau dos anúncios.
   */
  const cabeNoFloat = useCallback(
    (v: TemplateModel) => {
      if (floatMin === null && floatMax === null) return true;
      const faixa = v.exterior?.floatRange;
      if (!faixa) return true;
      return faixa[0] <= (floatMax ?? 1) && faixa[1] >= (floatMin ?? 0);
    },
    [floatMin, floatMax],
  );

  const variantesVisiveis = useMemo(() => {
    if (!aberta) return [];
    return aberta.variants.filter(
      (v) =>
        cabeNoFloat(v) &&
        (filtros.exteriors.length === 0 || (v.exterior && filtros.exteriors.includes(v.exterior.code))),
    );
  }, [aberta, cabeNoFloat, filtros.exteriors]);

  const proximaOrdem = () => {
    const i = ORDENS.findIndex((o) => o.key === ordem);
    setOrdem(ORDENS[(i + 1) % ORDENS.length]!.key);
  };
  const rotuloOrdem = ORDENS.find((o) => o.key === ordem)!.label;

  function voltar() {
    if (desgaste) setDesgaste(null);
    else setAberta(null);
  }

  return (
    <section className={`panel up-coluna direita ${recolhido ? 'fechada' : ''}`}>
      <header className="up-cabeca">
        <span className="up-cabeca-total">
          <i>🛒</i> {total}
        </span>
        <span className="up-cabeca-titulo">Você Recebe</span>
        <button className="up-chevron" onClick={onRecolher} title={recolhido ? 'Abrir' : 'Recolher'}>
          {recolhido ? '‹' : '›'}
        </button>
      </header>

      {!recolhido && (
        <>
          {children}

          <div className="up-barra">
            <button className="btn ghost small up-ordem" onClick={proximaOrdem} title="Trocar a ordenação">
              ⇅ {rotuloOrdem}
            </button>
            <div className="up-busca">
              <i>🔍</i>
              <input
                className="input"
                placeholder="Buscar"
                value={busca}
                onChange={(e) => {
                  setBusca(e.target.value);
                  setAberta(null);
                  setDesgaste(null);
                }}
              />
            </div>
            <button
              className="btn ghost small"
              onClick={() => void buscar(true)}
              disabled={carregando}
              title="Recarregar"
            >
              ↻
            </button>
          </div>

          {/* O degrau seguinte ao da coluna do meio: lá se escolhe a
              categoria, aqui a arma dentro dela. Repetir as categorias seria
              dizer duas vezes a mesma coisa; sem categoria marcada não há
              recorte que caiba numa linha, e a barra some. */}
          {armas.length > 0 && (
            <div className="up-chips-linha">
              <div className={`up-chips ${armasAbertas ? 'aberta' : ''}`}>
                <button className={`chip ${filtros.arma ? '' : 'on'}`} onClick={() => onFiltros({ arma: null })}>
                  Todas
                </button>
                {armas.map((a) => (
                  <button
                    key={a.code}
                    className={`chip ${filtros.arma === a.code ? 'on' : ''}`}
                    onClick={() => onFiltros({ arma: filtros.arma === a.code ? null : a.code })}
                  >
                    {a.name}
                  </button>
                ))}
              </div>
              {/* Vinte facas não cabem numa linha, e a rolagem lateral não
                  aparece: sem este botão o resto da lista simplesmente não
                  existe para quem olha. Fica fora do rolador de propósito,
                  para não sumir junto com os chips. */}
              <button
                className="chip up-chips-mais"
                onClick={() => setArmasAbertas((v) => !v)}
                title={armasAbertas ? 'Mostrar em uma linha' : `Ver as ${armas.length} opções`}
              >
                {armasAbertas ? '▲' : `+${armas.length} ▾`}
              </button>
            </div>
          )}

          {/* O mesmo degrau, para Agentes: onde as outras categorias listam
              armas, esta lista os lados. Sem botão "+N" porque são dois. */}
          {times.length > 0 && (
            <div className="up-chips-linha">
              <div className="up-chips">
                <button className={`chip ${filtros.time ? '' : 'on'}`} onClick={() => onFiltros({ time: null })}>
                  Todos
                </button>
                {times.map((t) => (
                  <button
                    key={t.key}
                    className={`chip ${filtros.time === t.key ? 'on' : ''}`}
                    onClick={() => onFiltros({ time: filtros.time === t.key ? null : t.key })}
                  >
                    {loc(t)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(aberta || desgaste) && (
            <div className="up-trilha">
              <button className="btn ghost small" onClick={voltar}>
                ‹ voltar
              </button>
              <b>
                {cleanName(aberta?.name)}
                {desgaste ? ` · ${desgaste.exterior?.pt ?? 'Sem desgaste'}` : ''}
              </b>
            </div>
          )}

          <div className="up-rolagem">
            {carregando && !itens && <Spinner label="Carregando skins…" />}

            {!aberta && itens && itens.length === 0 && (
              <div className="up-vazio">
                <h2>Você Recebe</h2>
                <p>
                  {filtros.soCredito && credito > 0
                    ? 'Nada nessa faixa. Desligue o filtro de crédito ou escolha mais skins de entrada.'
                    : 'Nada encontrado com esses filtros.'}
                </p>
              </div>
            )}

            {!aberta && !itens && !carregando && (
              <div className="up-vazio">
                <h2>Você Recebe</h2>
                <p>Selecione itens para receber do nosso inventário</p>
              </div>
            )}

            {/* Terceiro degrau: as peças de verdade, com float e adesivos. */}
            {desgaste && aberta ? (
              erroAnuncios ? (
                <p className="muted">{erroAnuncios}</p>
              ) : !anuncios ? (
                <Spinner label="Carregando os anúncios…" />
              ) : anuncios.length === 0 ? (
                // Nomear o filtro que esvaziou a lista: "nenhum anúncio"
                // sozinho parece falta de estoque, e o cliente desiste da skin
                // em vez de afrouxar o filtro.
                <p className="muted">
                  Nenhum anúncio à venda neste desgaste
                  {[
                    filtros.floatMin || filtros.floatMax ? 'nessa faixa de float' : null,
                    filtros.estilos.length > 0 ? 'nessa fase' : null,
                    filtros.comAdesivos ? 'com adesivo' : null,
                  ]
                    .filter(Boolean)
                    .map((t, i, todos) => (i === 0 ? ` ${t}` : i === todos.length - 1 ? ` e ${t}` : `, ${t}`))
                    .join('')}
                  .
                </p>
              ) : (
                <div className="up-grid">
                  {anuncios.map((a) => (
                    <UpgradeCard
                      key={a.commodityNo}
                      preco={money(a.price)}
                      nome={a.name ?? aberta.name}
                      imagem={a.imageUrl ?? a.iconUrl}
                      desgaste={a.exterior?.code ?? desgaste.exterior?.code}
                      statTrak={desgaste.isStatTrak}
                      float={a.float}
                      adesivos={[
                        ...a.stickers.map((s) => ({ imageUrl: s.imageUrl, name: s.name })),
                        ...a.charms.map((c) => ({ imageUrl: c.imageUrl, name: c.name })),
                      ]}
                      inspectUrl={a.inspectUrl}
                      rodape={
                        a.paintSeed !== null ? <span className="dim">pattern {a.paintSeed}</span> : null
                      }
                      title="Escolher esta peça"
                      onClick={() => {
                        onEscolher(desgaste, aberta, a);
                        setDesgaste(null);
                        setAberta(null);
                      }}
                    />
                  ))}
                </div>
              )
            ) : aberta ? (
              <div className="up-grid">
                {variantesVisiveis.map((v) => (
                  <UpgradeCard
                    key={v.id}
                    preco={money(v.price)}
                    nome={v.name}
                    imagem={v.iconUrl}
                    desgaste={v.exterior?.code}
                    statTrak={v.isStatTrak}
                    rodape={<span className="dim">{v.exterior?.pt ?? 'Sem desgaste'} · ver peças</span>}
                    title="Escolher o desgaste"
                    // Abre os anúncios daquele desgaste, em vez de adicionar
                    // o template direto: a peça é que vai no pedido.
                    onClick={() => setDesgaste(v)}
                  />
                ))}
                {variantesVisiveis.length === 0 && (
                  <p className="muted">Nenhum desgaste desta skin cabe nos filtros de float e exterior.</p>
                )}
              </div>
            ) : (
              itens && (
                <div className="up-grid">
                  {itens.map((g) => (
                    <UpgradeCard
                      key={g.key}
                      preco={money(g.minPrice)}
                      nome={g.name}
                      imagem={g.iconUrl}
                      statTrak={g.isStatTrak}
                      rodape={
                        <span className="dim">
                          {g.variantCount === 1 ? '1 desgaste' : `${g.variantCount} desgastes`}
                        </span>
                      }
                      title={g.variantCount === 1 ? 'Ver as peças' : 'Escolher o desgaste'}
                      onClick={() => {
                        setAberta(g);
                        // Desgaste único pula direto para os anúncios: não há
                        // escolha a fazer no meio.
                        if (g.variants.length === 1) setDesgaste(g.variants[0]!);
                      }}
                    />
                  ))}
                </div>
              )
            )}
          </div>
        </>
      )}
    </section>
  );
}
